import React, { useEffect, useState } from "react";
import { Button, Form } from "react-bootstrap";
import { useFormik, FormikHelpers } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
  useGetNewBarcodeMutation,
  useGetItemCreateDropdownValMutation,
} from "../../../redux/inventory/inventoryApiSlice";
import { getErrorMessage } from "../../../utils/commonUtils";

export interface CreateBarcodeFormValues {
  barcode: string;
  rxName: string;
  rxGroup: string;
  ndcCode: string;
  manufacturer: string;
  lotNumber: string;
  expirationDate: string;
  qty: string;
  inventoryUnit: string;
  itemType: string;
  source: string;
  orderingProduct: string;
  refInfo: string;
  notes: string;
}

interface CreateBarcodeFormProps {
  onSave: (values: CreateBarcodeFormValues) => void;
  onCancel: () => void;
}

const validationSchema = Yup.object({
  barcode: Yup.string().required("Barcode is required"),
  rxName: Yup.string().required("Rx Name is required"),
  rxGroup: Yup.string().required("Rx Group is required"),
  ndcCode: Yup.string().required("NDC Code is required"),
  manufacturer: Yup.string().required("Manufacturer is required"),
  lotNumber: Yup.string().required("Lot Number is required"),
  expirationDate: Yup.string().required("Expiration Date is required"),
  qty: Yup.string(),
  inventoryUnit: Yup.string().required("Inventory Unit is required"),
  itemType: Yup.string().required("Item Type is required"),
  source: Yup.string().required("Source is required"),
  orderingProduct: Yup.string(),
  refInfo: Yup.string(),
  notes: Yup.string(),
});

const initialValues: CreateBarcodeFormValues = {
  barcode: "",
  rxName: "",
  rxGroup: "",
  ndcCode: "",
  manufacturer: "",
  lotNumber: "",
  expirationDate: "",
  qty: "",
  inventoryUnit: "",
  itemType: "",
  source: "",
  orderingProduct: "",
  refInfo: "",
  notes: "",
};

const CreateBarcodeForm: React.FC<CreateBarcodeFormProps> = ({
  onSave,
  onCancel,
}) => {
  const [getNewBarcode, { isLoading: getNewBarcodeLoading }] =
    useGetNewBarcodeMutation();
  const [getDropdownVal] = useGetItemCreateDropdownValMutation();

  const [units, setUnits] = useState<any[]>([]);
  const [rxTypes, setRxTypes] = useState<any[]>([]);
  const [stocks, setStocks] = useState<any[]>([]);

  const {
    handleChange,
    handleBlur,
    values,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (
      values: CreateBarcodeFormValues,
      { setSubmitting }: FormikHelpers<CreateBarcodeFormValues>
    ) => {
      onSave(values);
      setSubmitting(false);
    },
  });

  const fetchNewBarcode = async () => {
    try {
      const response = await getNewBarcode({}).unwrap();
      if (response?.result) {
        setFieldValue("barcode", response.result);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const fetchDropdownValues = async () => {
    try {
      const response = await getDropdownVal({}).unwrap();
      if (response?.result) {
        const { units, rxTypes, stocks } = response.result;
        if (units) setUnits(units);
        if (rxTypes) setRxTypes(rxTypes);
        if (stocks) setStocks(stocks);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    fetchNewBarcode();
    fetchDropdownValues();
  }, []);

  return (
    <div className="create-barcode-content">
      <Form onSubmit={handleSubmit}>
        <div className="barcode-form-grid">
          {/* Row 1 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>Barcode: <span className="text-danger">*</span></label>
              <div className="input-with-icons">
                <Form.Control
                  size="sm"
                  name="barcode"
                  value={values.barcode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.barcode && !!errors.barcode}
                  disabled={getNewBarcodeLoading}
                  style={{ fontWeight: "bold", color: "#044d6e" }}
                />
                <i className="bi bi-search icon-btn" />
                <i
                  className="bi bi-arrow-clockwise icon-btn"
                  title="Refresh the barcode"
                  onClick={fetchNewBarcode}
                  style={{ cursor: "pointer" }}
                />
              </div>
              {touched.barcode && errors.barcode && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.barcode}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Rx Name: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                name="rxName"
                value={values.rxName}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.rxName && !!errors.rxName}
              />
              {touched.rxName && errors.rxName && (
                <Form.Control.Feedback type="invalid">
                  {errors.rxName}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Rx Group: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                name="rxGroup"
                value={values.rxGroup}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.rxGroup && !!errors.rxGroup}
              />
              {touched.rxGroup && errors.rxGroup && (
                <Form.Control.Feedback type="invalid">
                  {errors.rxGroup}
                </Form.Control.Feedback>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>NDC Code: <span className="text-danger">*</span></label>
              <div className="input-with-icons">
                <Form.Control
                  size="sm"
                  name="ndcCode"
                  value={values.ndcCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={touched.ndcCode && !!errors.ndcCode}
                />
                <i className="bi bi-search icon-btn" />
              </div>
              {touched.ndcCode && errors.ndcCode && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.ndcCode}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Manufacturer: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                name="manufacturer"
                value={values.manufacturer}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.manufacturer && !!errors.manufacturer}
              />
              {touched.manufacturer && errors.manufacturer && (
                <Form.Control.Feedback type="invalid">
                  {errors.manufacturer}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Lot Number: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                name="lotNumber"
                value={values.lotNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.lotNumber && !!errors.lotNumber}
              />
              {touched.lotNumber && errors.lotNumber && (
                <Form.Control.Feedback type="invalid">
                  {errors.lotNumber}
                </Form.Control.Feedback>
              )}
            </div>
          </div>

          {/* Row 3 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>Expiration Date: <span className="text-danger">*</span></label>
              <Form.Control
                size="sm"
                type="date"
                name="expirationDate"
                value={values.expirationDate}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.expirationDate && !!errors.expirationDate}
              />
              {touched.expirationDate && errors.expirationDate && (
                <Form.Control.Feedback type="invalid">
                  {errors.expirationDate}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Qty:</label>
              <Form.Control
                size="sm"
                type="number"
                name="qty"
                value={values.qty}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter Quantity"
              />
            </div>
            <div className="barcode-form-field">
              <label>Inventory Unit: <span className="text-danger">*</span></label>
              <Form.Select
                size="sm"
                name="inventoryUnit"
                value={values.inventoryUnit}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.inventoryUnit && !!errors.inventoryUnit}
              >
                <option value="">Select</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.unitName}
                  </option>
                ))}
              </Form.Select>
              {touched.inventoryUnit && errors.inventoryUnit && (
                <Form.Control.Feedback type="invalid">
                  {errors.inventoryUnit}
                </Form.Control.Feedback>
              )}
            </div>
          </div>

          {/* Row 4 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>Item Type: <span className="text-danger">*</span></label>
              <Form.Select
                size="sm"
                name="itemType"
                value={values.itemType}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.itemType && !!errors.itemType}
              >
                <option value="">Select</option>
                {rxTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.rxTypeName}
                  </option>
                ))}
              </Form.Select>
              {touched.itemType && errors.itemType && (
                <Form.Control.Feedback type="invalid">
                  {errors.itemType}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Source: <span className="text-danger">*</span></label>
              <Form.Select
                size="sm"
                name="source"
                value={values.source}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.source && !!errors.source}
              >
                <option value="">Select</option>
                {stocks.map((stock) => (
                  <option key={stock.id} value={stock.id}>
                    {stock.stockName}
                  </option>
                ))}
              </Form.Select>
              {touched.source && errors.source && (
                <Form.Control.Feedback type="invalid">
                  {errors.source}
                </Form.Control.Feedback>
              )}
            </div>
            <div className="barcode-form-field">
              <label>Ordering Product:</label>
              <Form.Control
                size="sm"
                name="orderingProduct"
                value={values.orderingProduct}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </div>

          {/* Row 5 */}
          <div className="barcode-form-row">
            <div className="barcode-form-field">
              <label>Ref Info:</label>
              <Form.Control
                size="sm"
                name="refInfo"
                value={values.refInfo}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
            <div className="barcode-form-field span-2">
              <label>Notes:</label>
              <Form.Control
                as="textarea"
                rows={3}
                size="sm"
                name="notes"
                value={values.notes}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>
          </div>
        </div>

        <div className="modal--action--btn--wrapper">
          <Button variant="primary" type="submit">
            Save
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreateBarcodeForm;
